import React, {
    useCallback, useEffect, useMemo, useRef, useReducer,
} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Dialog, makeStyles } from '@material-ui/core';
import { fade } from '@material-ui/core/styles';
import DialogAppBar from 'components/widgets/modal/DialogAppBar';
import DialogActions from 'components/widgets/modal/DialogActions';
import ButtonStyles from 'styles/theme/Button';
import CustomerService from 'services/modules/CustomerService';
import ModalUtils from 'utils/ModalUtils';
import DuplicateSuggestStep from 'components/modules/customer/merge/DuplicateSuggestStep';
import DuplicateResolveStep from 'components/modules/customer/merge/DuplicateResolveStep';
import DuplicateConfirmStep from 'components/modules/customer/merge/DuplicateConfirmStep';
import {
    DUPLICATE_DIALOG_MODE,
    DUPLICATE_DIALOG_STEP,
} from 'components/modules/customer/merge/duplicateDialogModes';

const useStyles = makeStyles((theme) => ({
    ...ButtonStyles.getStyle(theme),
    dialogPaper: {
        maxWidth: '96vw',
        width: 1120,
        display: 'flex',
        flexDirection: 'column',
    },
    suggestOnlyPaper: {
        maxWidth: 960,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    resolveWrapper: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
    },
    hidden: {
        display: 'none',
    },
    // Highest-priority progressive action (resolve / continue merge)
    primaryActionButton: {
        '&&': {
            backgroundColor: theme.palette.secondaryInfo.main,
            color: theme.palette.secondaryInfo.contrastText,
            boxShadow: 'none',
            border: '1px solid transparent',
            fontWeight: 600,
            '&:hover': {
                backgroundColor: theme.palette.secondaryInfo.dark,
                boxShadow: 'none',
            },
            '&.Mui-disabled': {
                backgroundColor: `rgba(${theme.palette.rgb.black}, 0.12)`,
                color: theme.palette.text.disabled,
                borderColor: 'transparent',
            },
        },
    },
    // Irreversible confirm — visual weight + caution
    destructiveActionButton: {
        '&&': {
            backgroundColor: theme.palette.error.main,
            color: theme.palette.error.contrastText,
            boxShadow: 'none',
            border: '1px solid transparent',
            fontWeight: 600,
            '&:hover': {
                backgroundColor: theme.palette.error.dark,
                boxShadow: 'none',
            },
            '&.Mui-disabled': {
                backgroundColor: `rgba(${theme.palette.rgb.black}, 0.12)`,
                color: theme.palette.text.disabled,
                borderColor: 'transparent',
            },
        },
    },
    // Affirmative alternate path (keep / use entered data)
    secondaryActionButton: {
        '&&': {
            backgroundColor: theme.palette.common.white,
            color: theme.palette.secondaryInfo.main,
            border: `1px solid ${theme.palette.secondaryInfo.main}`,
            boxShadow: 'none',
            fontWeight: 600,
            '&:hover': {
                backgroundColor: fade(
                    theme.palette.secondaryInfo.main,
                    theme.palette.action.hoverOpacity,
                ),
                boxShadow: 'none',
            },
            '&.Mui-disabled': {
                backgroundColor: theme.palette.common.white,
                color: theme.palette.text.disabled,
                borderColor: theme.palette.action.disabled,
            },
        },
    },
    // Low-priority / navigate / dismiss — outlined so it does not look disabled
    tertiaryActionButton: {
        '&&': {
            backgroundColor: theme.palette.common.white,
            color: theme.palette.text.purpure,
            border: `1px solid ${theme.palette.grey[400]}`,
            boxShadow: 'none',
            fontWeight: 600,
            '&:hover': {
                backgroundColor: theme.palette.grey[100],
                borderColor: theme.palette.grey[500],
                boxShadow: 'none',
            },
            '&.Mui-disabled': {
                backgroundColor: theme.palette.grey[100],
                color: theme.palette.text.disabled,
                borderColor: theme.palette.grey[300],
            },
        },
    },
}));

const normalizeSuggested = (suggestedCustomer) => {
    if (Array.isArray(suggestedCustomer)) return suggestedCustomer;
    if (suggestedCustomer) return [suggestedCustomer];
    return [];
};

const toSecondaryCodes = (customers) => customers
    .map((c) => Number(c.customerCode))
    .filter((code) => !Number.isNaN(code));

/**
 * Unified Suggest Customer + Customer Merge dialog.
 *
 * Modes:
 * - suggest: create/deals/opp/services — Use Entered / Use|Open Suggested
 * - edit: edit customer — Review and Merge + Use Entered / Use|Open Suggested → resolve
 * - mergeReview: banner Review and Merge — review list (non-selectable) → resolve
 * - mergeDirect: merge-with-current — resolve only
 */
const CustomerDuplicateDialog = ({
    open,
    mode,
    onClose,
    suggestedCustomer,
    enteredCustomer,
    onUseEntered,
    onUseSuggested,
    isCustomerModule,
    masterCustomerCode,
    secondaryCustomerCodes,
    onMergeCompleted,
}) => {
    const classes = useStyles();
    const customerService = useMemo(() => new CustomerService(), []);
    const confirmRequestRef = useRef(null);
    const executeMergeRef = useRef(null);

    const initialStep = mode === DUPLICATE_DIALOG_MODE.MERGE_DIRECT
        ? DUPLICATE_DIALOG_STEP.RESOLVE
        : DUPLICATE_DIALOG_STEP.SUGGEST;

    const initialState = {
        step: initialStep,
        selectedIndex: null,
        resolveSecondaryCodes: secondaryCustomerCodes || [],
        suggestEntered: enteredCustomer || {},
        resolveUi: {
            loading: true,
            ready: false,
            canMerge: false,
            merging: false,
        },
        confirmPayload: {
            secondaryCount: 0,
            previewRows: [],
        },
        canConfirmMerge: false,
    };

    const [state, setState] = useReducer((old, action) => ({ ...old, ...action }), initialState);
    const {
        step,
        selectedIndex,
        resolveSecondaryCodes,
        suggestEntered,
        resolveUi,
        confirmPayload,
        canConfirmMerge,
    } = state;

    const suggestedList = useMemo(
        () => normalizeSuggested(suggestedCustomer),
        [suggestedCustomer],
    );

    const usesResolve = mode !== DUPLICATE_DIALOG_MODE.SUGGEST;

    useEffect(() => {
        if (!open) return;
        setState({
            step: initialStep,
            selectedIndex: null,
            resolveSecondaryCodes: secondaryCustomerCodes || toSecondaryCodes(suggestedList),
            suggestEntered: enteredCustomer || {},
            resolveUi: {
                loading: true,
                ready: false,
                canMerge: false,
                merging: false,
            },
            confirmPayload: {
                secondaryCount: 0,
                previewRows: [],
            },
            canConfirmMerge: false,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        let active = true;
        if (!open || mode !== DUPLICATE_DIALOG_MODE.MERGE_REVIEW || !masterCustomerCode) {
            return undefined;
        }
        if (enteredCustomer && (enteredCustomer.firstName || enteredCustomer.lastName)) {
            return undefined;
        }

        customerService.getMergedCustomerInfo(masterCustomerCode)
            .then((response) => {
                if (!active) return;
                if (response.graphQLErrors) {
                    ModalUtils.errorMessage(response.graphQLErrors);
                    return;
                }
                const master = response.data?.getMergedCustomerInfo;
                if (!master) return;
                setState({
                    suggestEntered: {
                        firstName: master.firstName,
                        lastName: master.lastName,
                        email: master.email,
                        cellPhone: master.cellPhone,
                        address1: master.currentAddress?.address1,
                    },
                });
            })
            .catch((error) => {
                if (!active) return;
                ModalUtils.errorMessage(error?.graphQLErrors || [error]);
            });

        return () => {
            active = false;
        };
    }, [open, mode, masterCustomerCode, enteredCustomer, customerService]);

    const goToResolve = useCallback((codes) => {
        const validCodes = (codes || [])
            .map((code) => Number(code))
            .filter((code) => !Number.isNaN(code));
        if (validCodes.length === 0 || !masterCustomerCode) return;
        setState({
            resolveSecondaryCodes: validCodes,
            step: DUPLICATE_DIALOG_STEP.RESOLVE,
        });
    }, [masterCustomerCode]);

    const onReviewAndMerge = () => {
        goToResolve(toSecondaryCodes(suggestedList));
    };

    const onResolveDuplicate = () => {
        goToResolve(
            (secondaryCustomerCodes && secondaryCustomerCodes.length > 0)
                ? secondaryCustomerCodes
                : toSecondaryCodes(suggestedList),
        );
    };

    const onBack = () => {
        if (step === DUPLICATE_DIALOG_STEP.CONFIRM) {
            setState({
                step: DUPLICATE_DIALOG_STEP.RESOLVE,
                canConfirmMerge: false,
            });
            return;
        }
        setState({ step: DUPLICATE_DIALOG_STEP.SUGGEST });
    };

    const onResolveUiChange = useCallback((next) => {
        setState({ resolveUi: next });
    }, []);

    const onRequestConfirm = useCallback((payload) => {
        setState({
            confirmPayload: {
                secondaryCount: payload.secondaryCount || 0,
                previewRows: payload.previewRows || [],
            },
            canConfirmMerge: false,
            step: DUPLICATE_DIALOG_STEP.CONFIRM,
        });
    }, []);

    const onConfirmMerge = () => {
        if (typeof executeMergeRef.current === 'function') {
            executeMergeRef.current();
        }
    };

    const onCanConfirmChange = useCallback((next) => {
        setState({ canConfirmMerge: next });
    }, []);

    const activeCustomer = selectedIndex != null ? suggestedList[selectedIndex] : null;
    const isSuggestStep = step === DUPLICATE_DIALOG_STEP.SUGGEST;
    const isResolveStep = step === DUPLICATE_DIALOG_STEP.RESOLVE;
    const isConfirmStep = step === DUPLICATE_DIALOG_STEP.CONFIRM;
    const selectable = mode === DUPLICATE_DIALOG_MODE.SUGGEST
        || mode === DUPLICATE_DIALOG_MODE.EDIT;
    const showResolveBack = mode === DUPLICATE_DIALOG_MODE.MERGE_REVIEW
        || mode === DUPLICATE_DIALOG_MODE.EDIT;
    const showResolveContent = isResolveStep || isConfirmStep;

    let dialogTitle = 'Suggested Customer';
    if (isResolveStep) dialogTitle = 'Resolve Duplicate Records';
    if (isConfirmStep) dialogTitle = 'Confirm Merge';

    const renderFooter = () => {
        if (isSuggestStep && mode === DUPLICATE_DIALOG_MODE.MERGE_REVIEW) {
            return (
                <DialogActions
                    titlePrimary="Resolve Duplicate"
                    titleSecondary="Back"
                    titleAdditional="Cancel"
                    useAdditionalButton
                    onClickAdditional={onClose}
                    onClickSecondary={() => {}}
                    onClickPrimary={onResolveDuplicate}
                    disableSecondaryButton
                    classAdditionalButton={classes.tertiaryActionButton}
                    classSecondaryButton={classes.tertiaryActionButton}
                    classPrimaryButton={classes.primaryActionButton}
                />
            );
        }

        if (isSuggestStep && mode === DUPLICATE_DIALOG_MODE.EDIT) {
            return (
                <DialogActions
                    titlePrimary={isCustomerModule ? 'Open Suggested Customer' : 'Use Suggested Customer'}
                    titleSecondary="Use Entered Customer"
                    titleAdditional="Review and Merge"
                    useAdditionalButton
                    onClickAdditional={onReviewAndMerge}
                    onClickSecondary={onUseEntered}
                    onClickPrimary={() => onUseSuggested(activeCustomer)}
                    disablePrimaryButton={activeCustomer == null}
                    disableAdditionalButton={suggestedList.length === 0}
                    classAdditionalButton={classes.primaryActionButton}
                    classSecondaryButton={classes.secondaryActionButton}
                    classPrimaryButton={classes.tertiaryActionButton}
                />
            );
        }

        if (isSuggestStep) {
            return (
                <DialogActions
                    titlePrimary={isCustomerModule ? 'Open Suggested Customer' : 'Use Suggested Customer'}
                    titleSecondary="Use Entered Customer"
                    onClickSecondary={onUseEntered}
                    onClickPrimary={() => onUseSuggested(activeCustomer)}
                    classPrimaryButton={classes.primaryActionButton}
                    classSecondaryButton={classes.secondaryActionButton}
                    disablePrimaryButton={activeCustomer == null}
                />
            );
        }

        if (isConfirmStep) {
            return (
                <DialogActions
                    titlePrimary="Confirm Merge"
                    titleSecondary="Back"
                    titleAdditional="Cancel"
                    useAdditionalButton
                    onClickAdditional={onClose}
                    onClickSecondary={onBack}
                    onClickPrimary={onConfirmMerge}
                    disableAdditionalButton={resolveUi.merging}
                    disableSecondaryButton={resolveUi.merging}
                    disablePrimaryButton={resolveUi.merging || !canConfirmMerge}
                    classAdditionalButton={classes.tertiaryActionButton}
                    classSecondaryButton={classes.tertiaryActionButton}
                    classPrimaryButton={classes.destructiveActionButton}
                />
            );
        }

        // Resolve step
        if (showResolveBack) {
            return (
                <DialogActions
                    titlePrimary="Merge Records"
                    titleSecondary="Back"
                    titleAdditional="Cancel"
                    useAdditionalButton
                    onClickAdditional={onClose}
                    onClickSecondary={onBack}
                    onClickPrimary={() => confirmRequestRef.current?.()}
                    disableAdditionalButton={resolveUi.merging}
                    disableSecondaryButton={resolveUi.merging}
                    disablePrimaryButton={resolveUi.merging || !resolveUi.canMerge}
                    classAdditionalButton={classes.tertiaryActionButton}
                    classSecondaryButton={classes.tertiaryActionButton}
                    classPrimaryButton={classes.primaryActionButton}
                />
            );
        }

        return (
            <DialogActions
                titlePrimary="Merge Records"
                titleSecondary="Cancel"
                onClickSecondary={onClose}
                onClickPrimary={() => confirmRequestRef.current?.()}
                disableSecondaryButton={resolveUi.merging}
                disablePrimaryButton={resolveUi.merging || !resolveUi.canMerge}
                classSecondaryButton={classes.tertiaryActionButton}
                classPrimaryButton={classes.primaryActionButton}
            />
        );
    };

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth={usesResolve ? 'lg' : 'md'}
            onClose={resolveUi.merging ? undefined : onClose}
            PaperProps={{
                className: usesResolve ? classes.dialogPaper : classes.suggestOnlyPaper,
            }}
            aria-label={dialogTitle}
        >
            <DialogAppBar
                title={dialogTitle}
                onClose={onClose}
                iconSize="sm"
                disabled={resolveUi.merging}
            />

            {isSuggestStep && (
                <DuplicateSuggestStep
                    suggestedCustomer={suggestedList}
                    enteredCustomer={suggestEntered}
                    selectable={selectable}
                    selectedIndex={selectedIndex}
                    onSelectIndex={(idx) => setState({ selectedIndex: idx })}
                />
            )}

            {showResolveContent && masterCustomerCode && resolveSecondaryCodes.length > 0 && (
                <div className={clsx(classes.resolveWrapper, !isResolveStep && classes.hidden)}>
                    <DuplicateResolveStep
                        active={open && showResolveContent}
                        masterCustomerCode={Number(masterCustomerCode)}
                        secondaryCustomerCodes={resolveSecondaryCodes}
                        suggestedCustomer={suggestedList}
                        onClose={onClose}
                        onMergeCompleted={onMergeCompleted}
                        onUiChange={onResolveUiChange}
                        confirmRequestRef={confirmRequestRef}
                        onRequestConfirm={onRequestConfirm}
                        executeMergeRef={executeMergeRef}
                    />
                </div>
            )}

            {isConfirmStep && (
                <DuplicateConfirmStep
                    active={isConfirmStep}
                    masterCustomerCode={masterCustomerCode}
                    secondaryCount={confirmPayload.secondaryCount}
                    previewRows={confirmPayload.previewRows}
                    onCanConfirmChange={onCanConfirmChange}
                />
            )}

            {renderFooter()}
        </Dialog>
    );
};

CustomerDuplicateDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    mode: PropTypes.oneOf(Object.values(DUPLICATE_DIALOG_MODE)).isRequired,
    onClose: PropTypes.func.isRequired,
    suggestedCustomer: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    enteredCustomer: PropTypes.object,
    onUseEntered: PropTypes.func,
    onUseSuggested: PropTypes.func,
    isCustomerModule: PropTypes.bool,
    masterCustomerCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    secondaryCustomerCodes: PropTypes.arrayOf(PropTypes.number),
    onMergeCompleted: PropTypes.func,
};

CustomerDuplicateDialog.defaultProps = {
    suggestedCustomer: [],
    enteredCustomer: {},
    onUseEntered: () => {},
    onUseSuggested: () => {},
    isCustomerModule: false,
    masterCustomerCode: null,
    secondaryCustomerCodes: null,
    onMergeCompleted: () => {},
};

export default CustomerDuplicateDialog;
